"use client";

import { EditableValue } from "../forms/EditableValue";

/* -------------------------------------------------------------------------- */
/* TechRow                                                                     */
/* -------------------------------------------------------------------------- */

export interface TechRowProps {
  /** Technology category label (e.g. "CDI Solution"). */
  label: string;
  /**
   * Current vendor name.  `null` means no vendor is set and renders as
   * "None" with muted/italic styling.
   */
  vendor: string | null;
  /**
   * Called with the trimmed new string when the user commits an edit.
   * The caller is responsible for mapping empty strings / "None" to `null`.
   */
  onSave: (next: string) => void;
}

/**
 * A `<li>` row for the Tech Stack section.
 *
 * Shows the technology category on the left and an inline-editable vendor
 * name on the right.  An unset vendor is rendered in a muted style.
 */
export function TechRow({ label, vendor, onSave }: TechRowProps) {
  const isSet = vendor !== null;
  return (
    <li
      className={[
        "flex items-center gap-3",
        "min-h-[40px] py-2",
        "border-b border-(--border-default) last:border-b-0",
      ].join(" ")}
    >
      <span className="text-sm text-(--fg-primary) shrink-0">{label}</span>

      <span
        className={[
          "ml-auto text-sm",
          isSet ? "text-(--fg-primary)" : "text-(--fg-muted) italic",
        ].join(" ")}
      >
        <EditableValue
          value={vendor ?? "None"}
          align="right"
          onSave={onSave}
          ariaLabel={`Set vendor for ${label}`}
        />
      </span>
    </li>
  );
}
